import { FC, useEffect, useMemo, useState } from "react"
import { Link as ReactRouterLink, useNavigate } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { OpenInNew } from "@mui/icons-material"
import { DataGrid, Modal, SnackbarAlert } from "@/core/components"
import { getApiErrorMessage } from "@/core/utils"
import { UserbotBatches } from "../../interfaces"
import {
  useAccountBatchQuery,
  useCancelAccountBatchMutation,
  useRetryAccountBatchFailedMutation,
} from "../../store"
import { useClientsLookup, useUserbotWriteAccess } from "../../hooks"
import { ClientIdentity } from "../identity/clientIdentity"
import {
  BATCH_ITEM_STATUS_META,
  BATCH_JOB_STATUS_META,
  BATCH_POLL_MS,
  isAccountBatchTerminal,
} from "../../constants/batchStatusMeta"
import {
  describeBatchReason,
  dictionaryEmptyKind,
} from "../../constants/batchReasons"
import { describeBatchTarget } from "./batchTarget"

interface IProps {
  jobId: string
}

export const BatchProgress: FC<IProps> = ({ jobId }) => {
  const navigate = useNavigate()
  const { getClient } = useClientsLookup()
  const canWrite = useUserbotWriteAccess()
  const [pollingInterval, setPollingInterval] = useState(BATCH_POLL_MS)
  const [statusFilter, setStatusFilter] = useState<
    UserbotBatches.AccountBatchItemStatus | "all"
  >("all")

  // Items запрашиваются с limit=500 (максимум query и дефолтный кап
  // USERBOT_BATCH_MAX_ITEMS) — полный состав для фильтра и retry (спека §5.2).
  const {
    currentData: data,
    isFetching,
    isError,
    error,
    refetch,
  } = useAccountBatchQuery({ id: jobId, limit: 500 }, { pollingInterval })

  const [cancelBatch, { isLoading: isCancelling }] =
    useCancelAccountBatchMutation()
  const [retryFailed, { isLoading: isRetrying }] =
    useRetryAccountBatchFailedMutation()
  const [cancelOpen, setCancelOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error" | "warning"
    message: string
  } | null>(null)

  // Only a durable terminal GET settles the job; refresh conflicts preserve polling.
  useEffect(() => {
    if ((isError && !data) || (data && isAccountBatchTerminal(data.status))) {
      setPollingInterval(0)
      return
    }
    setPollingInterval(BATCH_POLL_MS)
  }, [data, isError])

  const items = useMemo(() => data?.items ?? [], [data])
  const isCapped = !!data && data.itemsTotal > items.length
  const filteredItems = useMemo(
    () =>
      statusFilter === "all"
        ? items
        : items.filter((i) => i.status === statusFilter),
    [items, statusFilter],
  )

  // Кандидаты retry: терминальные failed ∪ crash-сироты (pending без chainId).
  // При капе (itemsTotal > items.length) — по counters.failed ∪ сироты среди
  // загруженных (спека §5.2).
  const hasRetryCandidates = useMemo(() => {
    if (!data) return false
    const loadedFailed = items.some((i) => i.status === "failed")
    const loadedOrphan = items.some(
      (i) => i.status === "pending" && i.chainId === null,
    )
    if (isCapped) return data.counters.failed > 0 || loadedOrphan
    return loadedFailed || loadedOrphan
  }, [data, items, isCapped])

  const handleCancel = async () => {
    try {
      await cancelBatch({ id: jobId }).unwrap()
      refetch()
      setSnackbar({
        variant: "success",
        message: "Отмена запрошена; ожидаем сохранённый итоговый отчёт",
      })
    } catch (e) {
      if ((e as { status?: number }).status === 409) {
        // Джоба уже не running — дочитываем состояние из GET /:id (спека §5.2).
        refetch()
        setSnackbar({ variant: "warning", message: "Статус уже изменился" })
      } else {
        setSnackbar({ variant: "error", message: getApiErrorMessage(e) })
      }
    } finally {
      setCancelOpen(false)
    }
  }

  const handleRetry = async () => {
    try {
      const res = await retryFailed({ id: jobId }).unwrap()
      setSnackbar({
        variant: "success",
        message: `Переиграно: ${res.replayed}`,
      })
    } catch (e) {
      setSnackbar({ variant: "error", message: getApiErrorMessage(e) })
    }
  }

  const goBack = () => navigate("/userbot/batches")

  if (isError && !data)
    return (
      <Stack spacing={2}>
        <Alert severity="error">{getApiErrorMessage(error)}</Alert>
        <Button onClick={() => refetch()}>Обновить</Button>
        <Button onClick={goBack}>Назад к списку</Button>
      </Stack>
    )
  if (!data) return isFetching ? <CircularProgress size={24} /> : null

  const columns = [
    {
      field: "clientId",
      headerName: "Клиент",
      flex: 2,
      sortable: false,
      renderCell: ({ row }: { row: UserbotBatches.AccountBatchItem }) => {
        const client = getClient(row.clientId)
        return client ? (
          <ClientIdentity client={client} compact />
        ) : (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {row.clientId.slice(0, 8)}
          </Typography>
        )
      },
    },
    {
      field: "status",
      headerName: "Статус",
      flex: 1,
      sortable: false,
      renderCell: ({ row }: { row: UserbotBatches.AccountBatchItem }) => (
        <Chip
          size="small"
          label={BATCH_ITEM_STATUS_META[row.status]?.label ?? row.status}
          color={BATCH_ITEM_STATUS_META[row.status]?.color ?? "default"}
        />
      ),
    },
    {
      field: "deliveryState",
      headerName: "Допуск доставки",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }: { row: UserbotBatches.AccountBatchItem }) =>
        row.deliveryState ?? "Неизвестно (legacy, неоднозначно)",
    },
    {
      field: "scheduleDate",
      headerName: "Запланирована",
      flex: 1.5,
      sortable: false,
      valueGetter: ({ value }: { value?: string | null }) =>
        value ? new Date(value).toLocaleString("ru") : "—",
    },
    {
      field: "reason",
      headerName: "Причина",
      flex: 2,
      sortable: false,
      renderCell: ({ row }: { row: UserbotBatches.AccountBatchItem }) => {
        if (!row.reason) return "—"
        const view = describeBatchReason(row.reason)
        const kind = dictionaryEmptyKind(row.reason)
        return (
          <Stack spacing={0.5}>
            <Chip size="small" label={view.label} color={view.severity} />
            {kind && (
              <Typography variant="caption" color="text.secondary">
                Наполните{" "}
                <Link
                  component={ReactRouterLink}
                  to="/userbot/profile-dictionaries"
                >
                  словарь «{kind}»
                </Link>{" "}
                и повторите упавшие
              </Typography>
            )}
          </Stack>
        )
      },
    },
    {
      field: "failedReason",
      headerName: "Ошибка",
      flex: 2,
      sortable: false,
      renderCell: ({ row }: { row: UserbotBatches.AccountBatchItem }) =>
        row.status === "failed" ? (
          <Typography
            variant="caption"
            noWrap
            title={row.failedReason ?? undefined}
          >
            {row.failedReason ?? "—"}
          </Typography>
        ) : (
          "—"
        ),
    },
    {
      field: "attempt",
      headerName: "Попытка",
      flex: 0.7,
      sortable: false,
    },
    {
      field: "chainId",
      headerName: "Цепочка",
      flex: 0.7,
      sortable: false,
      renderCell: ({ row }: { row: UserbotBatches.AccountBatchItem }) =>
        row.chainId ? (
          <IconButton
            size="small"
            aria-label="Открыть отчёт цепочки"
            onClick={() => navigate(`/userbot/chains/report/${row.chainId}`)}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        ) : (
          "—"
        ),
    },
  ]

  return (
    <Stack spacing={2}>
      {isError && <Alert severity="error">{getApiErrorMessage(error)}</Alert>}
      <Button onClick={() => refetch()}>Обновить</Button>
      <Alert severity="info">
        Ожидающие, отложенные, припаркованные и отменяемые доставки ещё не
        завершены. Допуск доставки не заменяет сохранённый итоговый отчёт.
      </Alert>
      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Chip
          size="small"
          label={BATCH_JOB_STATUS_META[data.status]?.label ?? data.status}
          color={BATCH_JOB_STATUS_META[data.status]?.color ?? "default"}
        />
        <Chip
          size="small"
          label={`Успешно: ${data.counters.succeeded}`}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`Ошибки: ${data.counters.failed}`}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`Пропущено: ${data.counters.skipped}`}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`Отменено: ${data.counters.cancelled}`}
          variant="outlined"
        />
        <Chip
          size="small"
          label={`Запущено: ${data.counters.dispatched}`}
          variant="outlined"
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {`Пауза между запусками: ${data.staggerMs} мс · Цель: ${describeBatchTarget(data.targetSnapshot)} · Создал: ${data.createdBy ?? "—"}`}
      </Typography>

      {canWrite && (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="error"
            disabled={data.status !== "running" || isCancelling}
            onClick={() => setCancelOpen(true)}
          >
            Отменить
          </Button>
          <Button
            variant="outlined"
            disabled={
              data.status === "cancelled" || !hasRetryCandidates || isRetrying
            }
            onClick={handleRetry}
          >
            Повторить упавшие
          </Button>
        </Box>
      )}

      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <TextField
          select
          size="small"
          label="Статус"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(
              e.target.value as UserbotBatches.AccountBatchItemStatus | "all",
            )
          }
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">Все статусы</MenuItem>
          {(
            Object.keys(
              BATCH_ITEM_STATUS_META,
            ) as UserbotBatches.AccountBatchItemStatus[]
          ).map((s) => (
            <MenuItem key={s} value={s}>
              {BATCH_ITEM_STATUS_META[s].label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {isCapped && (
        <Alert severity="warning">
          {`Показаны первые ${items.length} из ${data.itemsTotal}`}
        </Alert>
      )}

      <Box height="65vh">
        <DataGrid<UserbotBatches.AccountBatchItem>
          columns={columns}
          rows={filteredItems}
          getRowId={(row) => row.id}
          loading={isFetching && !data}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
          }}
          pageSizeOptions={[50]}
        />
      </Box>

      <Box>
        <Button onClick={goBack}>Назад к списку</Button>
      </Box>

      {cancelOpen && (
        <Modal
          disableBackdropClose
          title="Отменить запуск?"
          onClose={() => setCancelOpen(false)}
          element={
            <Box>
              <Typography>
                Отмена не прерывает выполняющийся RPC и не означает завершения.
                История и восстановление сохраняются до итогового отчёта;
                финализация возможна только в неактивной безопасной точке.
              </Typography>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={() => setCancelOpen(false)}>
                  Не отменять
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  disabled={isCancelling}
                  onClick={handleCancel}
                >
                  Отменить запуск
                </Button>
              </Box>
            </Box>
          }
        />
      )}
      <SnackbarAlert
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
        onClose={() => setSnackbar(null)}
      />
    </Stack>
  )
}
